/**
 * Implement a class that represents a Cohere AI client
 * and provides a set of tools for converting messages to and from the LLM.
 * - sendThread(thread: Thread): Thread - send a message to the LLM
 * - streamThread(thread: Thread): AsyncGenerator<string, Thread> - stream a message to the LLM
 */

import { CohereClient } from "cohere-ai";
import { nanoid } from "nanoid";
import zodToJsonSchema from "zod-to-json-schema";
import { Message, ToolCall } from "../message";
import { Thread } from "../thread";
import { Tool } from "../tool";
import { BaseModelDriver } from "./base";

// Constants
const MAX_PROMPT_CHARACTERS = 100_000;

/**
 * Available Cohere models
 */
export type CohereModel =
  | "command-a-03-2025"
  | "command-r7b-12-2024"
  | "command-r-plus-08-2024"
  | "command-r-plus-04-2024"
  | "command-r-plus"
  | "command-r-08-2024"
  | "command-r-03-2024"
  | "command-r"
  | "command"
  | "command-light"
  | "command-light-nightly";

/**
 * Configuration options for the Cohere driver
 */
export interface CohereConfig {
  /**
   * Cohere API Key
   */
  apiKey?: string;

  /**
   * Model to use
   * @default "command-r-plus"
   */
  model?: CohereModel;

  /**
   * Temperature for response generation
   * @default 0.7
   */
  temperature?: number;

  /**
   * Maximum number of tokens to generate
   */
  maxTokens?: number;

  /**
   * Whether to cache responses
   * @default false
   */
  cache?: boolean;
  
  /**
   * Enable tracing for this driver
   * @default false
   */
  trace?: boolean;
  
  /**
   * Additional metadata for tracing
   */
  traceMetadata?: Record<string, unknown>;
}

/**
 * Cohere API token counting
 */
interface TokenCounting {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

/**
 * Represents a Cohere AI client and provides tools for converting messages to/from the LLM
 */
export class CohereThreadDriver extends BaseModelDriver {
  /**
   * Cohere API Client
   */
  private client: CohereClient;

  /**
   * Cohere model to use
   */
  private model: CohereModel | string;

  /**
   * Temperature for response generation
   */
  private temperature: number;

  /**
   * Maximum number of tokens to generate
   */
  private maxTokens?: number;

  /**
   * Whether to cache responses
   */
  private cache: boolean;

  /**
   * Returns a list of available Cohere models
   */
  static getAvailableModels(): CohereModel[] {
    return [
      "command-a-03-2025",
      "command-r7b-12-2024",
      "command-r-plus-08-2024",
      "command-r-plus-04-2024",
      "command-r-plus",
      "command-r-08-2024",
      "command-r-03-2024",
      "command-r",
      "command",
      "command-light",
      "command-light-nightly"
    ];
  }

  /**
   * Create a new Cohere driver
   * @param config Configuration options
   */
  constructor(config?: CohereConfig) {
    super();

    // Get API Key from config or environment variable
    const apiKey = config?.apiKey || process.env.COHERE_API_KEY;
    if (!apiKey) {
      throw new Error(
        "Cohere API Key must be provided in config or as COHERE_API_KEY environment variable"
      );
    }

    // Create base client
    const baseClient = new CohereClient({ token: apiKey });
    
    // Only wrap with tracing if trace is enabled
    if (config?.trace ?? false) {
      // Create a proxy around the Cohere client to intercept API calls
      this.client = new Proxy(baseClient, {
        get: (target, prop, receiver) => {
          const value = Reflect.get(target, prop, receiver);
          console.log(`[CohereTracer] Intercepted call to ${String(prop)} with value:`, value);
          // Check if the property is a method we want to trace
          if ((prop === 'chat' || prop === 'chatStream') && typeof value === 'function') {
            // Import traceable directly to avoid circular dependencies
            const { traceable } = require('../tracing');
            
            return traceable(value.bind(target), {
              run_type: "llm",
              name: "cohere",
              ...config?.traceMetadata,
              aggregator: (args: any[], result: any) => {
                // Extract the request parameters
                const params = args[0] || {};
                
                // For streaming responses (chatStream)
                if (prop === 'chatStream') {
                  return {
                    model: params.model,
                    input: {
                      message: params.message,
                      chat_history: params.chat_history,
                      temperature: params.temperature,
                      max_tokens: params.max_tokens,
                      tools: params.tools ? params.tools.length : 0,
                    },
                    streaming: true
                  };
                }
                
                // For standard chat responses
                return {
                  model: params.model,
                  input: {
                    message: params.message,
                    chat_history: params.chat_history,
                    temperature: params.temperature,
                    max_tokens: params.max_tokens,
                    tools: params.tools ? params.tools.length : 0,
                  },
                  output: result.text,
                  metrics: result.meta?.billing_tokens ? {
                    prompt_tokens: result.meta.billing_tokens.input_tokens,
                    completion_tokens: result.meta.billing_tokens.output_tokens,
                    total_tokens: result.meta.billing_tokens.input_tokens + result.meta.billing_tokens.output_tokens,
                  } : undefined,
                  tool_calls: result.tool_calls?.length
                };
              }
            });
          }
          
          return value;
        }
      });
    } else {
      this.client = baseClient;
    }
    
    // Set configuration options
    this.model = config?.model || "command-r-plus";
    this.temperature = config?.temperature ?? 0.7;
    this.maxTokens = config?.maxTokens;
    this.cache = config?.cache ?? false;
  }

  /**
   * Send a thread to the LLM and get a response
   * @param thread Thread to send
   * @returns Updated thread with LLM response
   */
  async sendThread(thread: Thread): Promise<Thread> {
    // Validate thread
    if (thread.all().length === 0) {
      throw new Error("Cannot send an empty thread");
    }

    // Calculate total content length to prevent exceeding limits
    const totalContent = thread.all().map(m => m.content || "").join("\n");
    if (totalContent.length > MAX_PROMPT_CHARACTERS) {
      throw new Error(
        `The thread content (${totalContent.length} characters) exceeds the maximum allowed (${MAX_PROMPT_CHARACTERS})`
      );
    }

    try {
      const { messages, system } = this.convertMessagesToCohereFormat(thread);
      
      if (messages.length === 0) {
        throw new Error("No valid messages to send");
      }
      
      // Create a safe last message
      const lastMessage = messages[messages.length - 1];
      if (!lastMessage) {
        throw new Error("Failed to extract last message");
      }
      
      // Define chat options with tool definitions if tools are provided
      const chatOptions: any = {
        message: lastMessage.message,
        chat_history: messages.length > 1 ? messages.slice(0, -1) : [],
        model: this.model,
        temperature: this.temperature,
      };
      
      // Add optional parameters
      if (system) {
        chatOptions.preamble = system;
      }
      
      if (this.maxTokens) {
        chatOptions.max_tokens = this.maxTokens;
      }
      
      // Add tools if provided
      const tools = thread.getTools?.() || [];
      if (tools.length > 0) {
        chatOptions.tools = this.createToolDefinitions(tools);
      }

      // Send request to Cohere API
      const response = await this.client.chat(chatOptions);
      
      // Log debug information about token usage if available
      if (response.meta && 'billing_tokens' in response.meta) {
        const billingTokens = response.meta.billing_tokens as any;
        const tokens: TokenCounting = {
          inputTokens: billingTokens.input_tokens,
          outputTokens: billingTokens.output_tokens,
          totalTokens: 
            billingTokens.input_tokens + 
            billingTokens.output_tokens,
        };
        
        this.debug("Cohere API usage metrics", tokens);
      }
      
      // Create the assistant message from the response
      const message = new Message({
        role: "assistant",
        content: response.text,
        toolCalls: this.parseToolCalls(response),
        cache: this.cache,
      });
      
      // Add the message to the thread
      thread.push(message);

      return thread;
    } catch (error) {
      this.error(error);
      throw error;
    }
  }

  /**
   * Stream a thread to the LLM and get a streaming response
   * @param thread Thread to send
   * @returns AsyncGenerator yielding the stream and updated thread
   */
  async *streamThread(thread: Thread): AsyncGenerator<
    { stream: AsyncGenerator<string, void>; message: Message }, Thread
  > {
    // Validate thread
    if (thread.all().length === 0) {
      throw new Error("Cannot stream an empty thread");
    }

    // Calculate total content length to prevent exceeding limits
    const totalContent = thread.all().map(m => m.content || "").join("\n");
    if (totalContent.length > MAX_PROMPT_CHARACTERS) {
      throw new Error(
        `The thread content (${totalContent.length} characters) exceeds the maximum allowed (${MAX_PROMPT_CHARACTERS})`
      );
    }

    try {
      const { messages, system } = this.convertMessagesToCohereFormat(thread);
      
      if (messages.length === 0) {
        throw new Error("No valid messages to send");
      }
      
      // Create a safe last message
      const lastMessage = messages[messages.length - 1];
      if (!lastMessage) {
        throw new Error("Failed to extract last message");
      }
      
      // Define chat options with tool definitions if tools are provided
      const chatOptions: any = {
        message: lastMessage.message,
        chat_history: messages.length > 1 ? messages.slice(0, -1) : [],
        model: this.model,
        temperature: this.temperature,
      };
      
      // Add optional parameters
      if (system) {
        chatOptions.preamble = system;
      }
      
      if (this.maxTokens) {
        chatOptions.max_tokens = this.maxTokens;
      }
      
      // Add tools if provided
      const tools = thread.getTools?.() || [];
      if (tools.length > 0) {
        chatOptions.tools = this.createToolDefinitions(tools);
      }

      // Create response message
      const message = new Message({
        role: "assistant",
        content: "",
        cache: this.cache,
      });

      // Stream generator to yield content chunks
      const stream = this.createStreamGenerator(
        thread,
        chatOptions,
        message
      );

      // First yield the stream and message
      yield { stream, message };

      // Add message to thread
      thread.push(message);

      // Then yield the final thread
      return thread;
    } catch (error) {
      this.error(error);
      throw error;
    }
  }

  /**
   * Create a stream generator for handling streaming responses
   */
  private createStreamGenerator(
    thread: Thread,
    chatOptions: any,
    message: Message
  ): AsyncGenerator<string, void> {
    return (async function* (this: CohereThreadDriver) {
      try {
        // Initiate the streaming chat
        const streamingResponse = await this.client.chatStream(chatOptions);
        
        // Accumulate content for the message
        let accumulatedContent = "";
        let toolCalls: any[] = [];
        
        // Process each chunk from the stream
        for await (const chunk of streamingResponse) {
          // If the chunk has text, yield it and update the message
          if (chunk.eventType === "text-generation") {
            const textChunk = chunk.text || "";
            accumulatedContent += textChunk;
            
            // Create new message instead of modifying the existing one
            const updatedMessage = new Message({
              role: "assistant",
              content: accumulatedContent,
              cache: message.cache,
            });
            
            // Replace message in thread (reference should be updated by caller)
            const index = thread.all().indexOf(message);
            if (index >= 0) {
              thread.all()[index] = updatedMessage;
            }
            
            yield textChunk;
          }
          
          // If the chunk contains tool-call events (checking exact event type)
          if (chunk.eventType === "tool-calls-generation" || chunk.eventType === "tool-calls-chunk") {
            // Safely handle tools property with type checking
            const anyChunk = chunk as any; // Cast to any to bypass type checking
            if (anyChunk.tools && Array.isArray(anyChunk.tools)) {
              toolCalls.push(anyChunk.tools);
            }
          }
          
          // Update token metrics if available
          if (chunk.eventType === "stream-end" && 
              chunk.response?.meta && 
              'billing_tokens' in chunk.response.meta) {
            const billingTokens = chunk.response.meta.billing_tokens as any;
            const tokens: TokenCounting = {
              inputTokens: billingTokens.input_tokens,
              outputTokens: billingTokens.output_tokens,
              totalTokens: 
                billingTokens.input_tokens + 
                billingTokens.output_tokens,
            };
            
            this.debug("Cohere API usage metrics", tokens);
          }
        }
        
        // Process tool calls if any were returned
        if (toolCalls.length > 0) {
          // Format tool calls for the message
          const formattedToolCalls = toolCalls.flatMap(toolCall => {
            return toolCall.map((tool: any) => ({
              name: tool.name,
              toolUseId: nanoid(),
              arguments: JSON.parse(tool.input || "{}"),
            }));
          });
          
          // Create final message with both content and tool calls
          const finalMessage = new Message({
            role: "assistant",
            content: accumulatedContent,
            toolCalls: formattedToolCalls,
            cache: message.cache,
          });
          
          // Replace message in thread
          const index = thread.all().indexOf(message);
          if (index >= 0) {
            thread.all()[index] = finalMessage;
          }
        }
      } catch (error) {
        this.error(error);
        throw error;
      }
    }).bind(this)();
  }

  /**
   * Convert a thread's messages to Cohere's API format
   * @param thread Thread to convert
   * @returns Object containing messages array and optional system message
   */
  private convertMessagesToCohereFormat(
    thread: Thread
  ): { messages: { role: string; message: string; }[]; system?: string } {
    const threadMessages = thread.all();
    const formattedMessages: { role: string; message: string }[] = [];
    let systemMessage: string | undefined;
    
    // Process each message in the thread
    for (const threadMsg of threadMessages) {
      // Skip undefined content
      if (threadMsg.content === undefined) {
        continue;
      }
      
      // Handle system message (Cohere uses preamble for system messages)
      if (threadMsg.role === "system") {
        systemMessage = threadMsg.content;
        continue;
      }
      
      // Determine message role
      let role: string;
      if (threadMsg.role === "user") {
        role = "USER";
      } else if (threadMsg.role === "assistant") {
        role = "CHATBOT";
      } else {
        // Skip unknown roles
        continue;
      }
      
      // Create message content, merging tool results if present
      let message = threadMsg.content;
      
      // Add tool results content if present
      if (threadMsg.toolResults && threadMsg.toolResults.length > 0) {
        const resultContent = threadMsg.toolResults
          .map(result => {
            return `Tool result for ${result.name}:\n${JSON.stringify(result.result)}`;
          })
          .join("\n\n");
        
        message = message 
          ? `${message}\n\n${resultContent}`
          : resultContent;
      }
      
      formattedMessages.push({ role, message });
    }
    
    return { messages: formattedMessages, system: systemMessage };
  }

  /**
   * Create tool definitions from thread tools
   * @param tools Tools to convert
   * @returns Array of tool definitions for Cohere API
   */
  private createToolDefinitions(tools: Tool[]): any[] {
    if (!tools || tools.length === 0) {
      return [];
    }
    
    return tools.map(tool => {
      // Get schema as object
      const schema = zodToJsonSchema(tool.getParameters());
      
      // Cast schema to any to safely access properties
      const schemaObj: any = schema;
      const parameters = schemaObj.properties || {};
      
      return {
        name: tool.getName(),
        description: tool.getDescription(),
        parameter_definitions: parameters
      };
    });
  }

  /**
   * Parse tool calls from Cohere API response
   * @param response Cohere API response
   * @returns Array of tool calls if present
   */
  private parseToolCalls(response: any): ToolCall[] | undefined {
    // Check if the response has tool calls
    if (!response.tool_calls || response.tool_calls.length === 0) {
      return undefined;
    }
    
    // Map tool calls to the internal format
    return response.tool_calls.map((toolCall: any) => {
      let args = {};
      
      // Safely parse parameters
      if (toolCall.parameters) {
        try {
          // Handle if parameters is already an object
          if (typeof toolCall.parameters === 'object') {
            args = toolCall.parameters;
          } else {
            // Try to parse if it's a string
            args = JSON.parse(toolCall.parameters);
          }
        } catch (e) {
          this.error(`Failed to parse tool call parameters: ${e}`);
          // Return empty object on error
          args = {};
        }
      }
      
      return {
        name: toolCall.name,
        toolUseId: nanoid(),
        arguments: args,
      };
    });
  }
} 